import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Hammer, ServerCog, Bot, TrendingUp, Check, ArrowRight } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";

const PILLARS = [
  {
    key: "build", num: "01", title: "BUILD", subtitle: "Custom Software Engineering", Icon: Hammer,
    positioning: "We design and engineer scalable digital products built around your business.",
    caps: ["Websites & digital experiences", "Web applications", "Enterprise portals", "SaaS platforms", "Mobile-responsive applications", "API development", "Commerce platforms", "System integrations", "Modern application architecture"],
  },
  {
    key: "operate", num: "02", title: "OPERATE", subtitle: "Managed Digital Operations", Icon: ServerCog,
    positioning: "We keep critical digital systems secure, reliable and continuously improving.",
    caps: ["Cloud hosting", "Infrastructure", "DevOps", "Monitoring", "Security", "Backups", "Performance optimization", "Application maintenance", "Technical support", "Reliability engineering"],
  },
  {
    key: "automate", num: "03", title: "AUTOMATE", subtitle: "AI & Intelligent Automation", Icon: Bot,
    positioning: "We turn repetitive work into intelligent, autonomous workflows.",
    caps: ["AI agents", "Agentic workflows", "AI assistants", "Business process automation", "Document intelligence", "Sales automation", "Customer service AI", "Knowledge assistants", "Marketing automation", "Enterprise workflow automation"],
  },
  {
    key: "grow", num: "04", title: "GROW", subtitle: "Digital Growth & Optimization", Icon: TrendingUp,
    positioning: "We turn digital platforms into measurable engines for growth.",
    caps: ["SEO", "Analytics", "Conversion optimization", "Customer experience", "Personalization", "Digital marketing technology", "AI-driven insights", "Performance strategy"],
  },
];

export const WhatWeDo = () => {
  const [active, setActive] = useState("build");
  const current = PILLARS.find((p) => p.key === active);

  return (
    <section id="what-we-do" data-testid="what-we-do-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeading
          testId="what-we-do"
          overline="What We Do"
          title={<>From strategy to <span className="jx-gradient-text">intelligent operations.</span></>}
        />

        <div className="mt-16 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 flex flex-col gap-3" role="tablist" aria-label="Service pillars">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.key} delay={i * 0.08}>
                <button
                  role="tab"
                  aria-selected={active === p.key}
                  data-testid={`pillar-tab-${p.key}`}
                  onClick={() => setActive(p.key)}
                  className={`w-full text-left rounded-2xl border p-6 transition-[border-color,background-color,transform,box-shadow] duration-400 group ${
                    active === p.key
                      ? "border-transparent jx-gradient-bg text-white shadow-[0_20px_50px_rgba(78,33,120,0.25)]"
                      : "bg-white border-jx-ink/8 hover:border-jx-purple/30 hover:-translate-y-0.5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className={`text-xs font-bold tracking-widest ${active === p.key ? "text-white/60" : "text-jx-ink/30"}`}>{p.num}</span>
                      <div>
                        <p className={`font-display font-extrabold text-2xl tracking-tight ${active === p.key ? "text-white" : "text-jx-ink"}`}>{p.title}</p>
                        <p className={`text-sm mt-0.5 ${active === p.key ? "text-white/70" : "text-jx-ink/50"}`}>{p.subtitle}</p>
                      </div>
                    </div>
                    <p.Icon className={`w-6 h-6 shrink-0 transition-transform duration-300 group-hover:scale-110 ${active === p.key ? "text-white" : "text-jx-magenta"}`} strokeWidth={1.8} />
                  </div>
                </button>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <div data-testid="pillar-detail-panel" className="h-full rounded-3xl bg-white border border-jx-ink/8 p-8 sm:p-12 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full jx-gradient-bg opacity-10 blur-2xl" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                >
                  <p className="jx-overline text-jx-magenta">{current.subtitle}</p>
                  <p data-testid="pillar-positioning" className="mt-4 font-display font-bold text-2xl sm:text-3xl tracking-tight text-jx-ink leading-snug">
                    {current.positioning}
                  </p>
                  <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {current.caps.map((c, i) => (
                      <motion.div
                        key={c}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + i * 0.04, duration: 0.4 }}
                        className="flex items-center gap-3 text-sm text-jx-ink/70"
                        data-testid={`pillar-cap-${current.key}-${i}`}
                      >
                        <Check className="w-4 h-4 text-jx-copper shrink-0" strokeWidth={2.5} />
                        {c}
                      </motion.div>
                    ))}
                  </div>
                  <Link
                    to={`/services/${current.key}`}
                    data-testid={`pillar-deep-link-${current.key}`}
                    className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-jx-purple hover:text-jx-magenta transition-colors"
                  >
                    Explore {current.title} in depth
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
